import {CATEGORIAS_FOLHA as categoria} from "../../shared/categoriasFolha";
import {useState} from "react";
import {
    Alert,
    Button,
    Checkbox,
    Stack,
    Table,
    TableContainer,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Link
} from "@mui/material";
import {Link as RouterLink} from "react-router-dom";
import {useDialog} from "muibox";
import {
    useAutorizarCompetenciaFolha,
    useConfiguracaoEntrega,
    useFolhasPreparadas
} from "../../api/DpgeIntegracaoEsfingeApi";
import {AMBIENTE} from "../../shared/env";
import {canAuthorizeFolha} from "../../shared/keycloak";
import {
    ambientePermiteAutorizacao,
    obterAmbienteAplicacao,
    obterModoEnvio
} from "../../shared/modoAplicacao";

const grupo = (tipo) =>
    [11, 12, 14].includes(tipo)
        ? "Inativos"
        : [21, 22, 24].includes(tipo)
        ? "Pensionistas"
        : "Ativos";
export default function ConjuntoFolhasSelecao({data, ambiente = AMBIENTE}) {
    const ambientePermitido = ambientePermiteAutorizacao(ambiente);
    const perfilPermitido = canAuthorizeFolha();
    const configuracao = useConfiguracaoEntrega();
    const modo = obterModoEnvio(configuracao.data, configuracao);
    const destino = configuracao.data?.destino;
    const destinoDisponivel =
        !configuracao.isLoading &&
        !configuracao.isError &&
        configuracao.data?.enabled === true &&
        ["ESFINGE_DIRETO", "FORNECEDOR"].includes(destino);
    const [selecionadas, setSelecionadas] = useState([]);
    const [justificativa, setJustificativa] = useState("");
    const [autorizadas, setAutorizadas] = useState([]);
    const [erro, setErro] = useState("");
    const autorizar = useAutorizarCompetenciaFolha();
    const dialog = useDialog();
    const folhas = data.folhas || [];
    const alertasRecorte = [
        ...new Set(
            folhas
                .flatMap((f) => f.alertas || [])
                .filter(
                    (a) =>
                        a.startsWith("Recorte de homologação") ||
                        a.startsWith("Escopo de envio:")
                )
                .map((a) => a.replace(" por solicitação do responsável", ""))
        )
    ];
    const consultaFolhas = useFolhasPreparadas();
    const resumos = consultaFolhas.data || [];
    const consultaDisponivel =
        !consultaFolhas.isLoading && !consultaFolhas.isError;
    const permiteAutorizar =
        ambientePermitido &&
        perfilPermitido &&
        destinoDisponivel &&
        consultaDisponivel;
    const moeda = (c) =>
        new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL"
        }).format(c / 100);
    const aptas = folhas
        .flatMap((f) => f.ocorrencias || [])
        .filter((o) => {
            const resumo = resumos.find((r) => r.id === o.id);
            return (
                permiteAutorizar &&
                resumo?.estado === "AGUARDANDO_APROVACAO" &&
                !resumo.arquivada &&
                !resumo.concluida &&
                !autorizadas.includes(o.id)
            );
        })
        .map((o) => o.id);
    const ids = selecionadas.filter((id) => aptas.includes(id));
    const justificativaValida =
        justificativa.trim().length >= 10 && justificativa.trim().length <= 500;
    async function enviar() {
        if (
            !permiteAutorizar ||
            !ids.length ||
            !justificativaValida ||
            autorizar.isLoading
        )
            return;
        try {
            await dialog.confirm({
                title: `Autorizar a seleção em ${
                    obterAmbienteAplicacao(ambiente).rotulo
                }?`,
                message:
                    "Competência " +
                    data.anoMes +
                    ": " +
                    ids.length +
                    " folhas selecionadas. Destino: " +
                    modo.rotulo +
                    ". " +
                    (destino === "FORNECEDOR"
                        ? "Cada ocorrência será entregue como um pacote ao fornecedor. O recebimento não comprova aceite do TCE-MS; acompanhe a reconciliação oficial."
                        : "O conector executará abertura, lotes e finalização no e-Sfinge HOM. Confira o resultado final de cada remessa.")
            });
        } catch {
            return;
        }
        setErro("");
        try {
            await autorizar.mutateAsync({
                anoMes: data.anoMes,
                ocorrenciaIds: ids,
                justificativa: justificativa.trim()
            });
            setAutorizadas((atual) => [...atual, ...ids]);
            setSelecionadas([]);
        } catch (e) {
            setErro(e.message || "Não foi possível autorizar a seleção.");
        }
    }
    return (
        <Stack spacing={2}>
            <Typography variant="h6">Folhas disponíveis para envio</Typography>
            <Alert severity="info">
                Selecione as folhas preparadas que deseja enviar. Pendências
                bloqueiam somente a respectiva folha. Os valores e o
                processamento podem ser conferidos pelo link de cada ocorrência.
            </Alert>
            {alertasRecorte.map((alerta) => (
                <Alert severity="warning" key={alerta}>
                    {alerta}
                </Alert>
            ))}
            <Button
                onClick={() => setSelecionadas(aptas)}
                disabled={!aptas.length}
            >
                Selecionar todas as aptas
            </Button>
            <TableContainer sx={{overflowX: "auto", maxWidth: "100%"}}>
                <Table
                    size="small"
                    aria-label="Folhas disponíveis da competência"
                >
                    <TableHead>
                        <TableRow>
                            <TableCell>Enviar</TableCell>
                            <TableCell>Folha e grupo</TableCell>
                            <TableCell>Beneficiários</TableCell>
                            <TableCell>Lançamentos</TableCell>
                            <TableCell>
                                Proventos / descontos / líquido
                            </TableCell>
                            <TableCell>Situação</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {folhas.map((f) => {
                            const o = f.ocorrencias?.[0];
                            const resumo = resumos.find((r) => r.id === o?.id);
                            const apta = o && aptas.includes(o.id);
                            const estado = resumo?.estado || o?.estadoEnvio;
                            return (
                                <TableRow
                                    key={
                                        f.categoriaFolha +
                                        ":" +
                                        f.tipoFolha +
                                        ":" +
                                        f.seqFolhaPagamento
                                    }
                                >
                                    <TableCell>
                                        <Checkbox
                                            inputProps={{
                                                "aria-label":
                                                    "Enviar " +
                                                    (categoria[
                                                        f.categoriaFolha
                                                    ] || f.categoriaFolha) +
                                                    " de " +
                                                    grupo(f.tipoFolha)
                                            }}
                                            disabled={!apta}
                                            checked={Boolean(
                                                apta && ids.includes(o.id)
                                            )}
                                            onChange={(e) =>
                                                setSelecionadas((atual) =>
                                                    e.target.checked
                                                        ? [...atual, o.id]
                                                        : atual.filter(
                                                              (id) =>
                                                                  id !== o.id
                                                          )
                                                )
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {categoria[f.categoriaFolha] ||
                                            f.categoriaFolha}{" "}
                                        · {grupo(f.tipoFolha)}
                                        <Typography
                                            variant="caption"
                                            display="block"
                                        >
                                            Tipo {f.tipoFolha} · sequência{" "}
                                            {f.seqFolhaPagamento}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {f.quantidadeServidores}
                                    </TableCell>
                                    <TableCell>
                                        {f.quantidadeLancamentos}
                                    </TableCell>
                                    <TableCell>
                                        {f.totalProventos != null &&
                                        f.totalDescontos != null ? (
                                            <>
                                                {moeda(
                                                    Math.round(
                                                        Number(
                                                            f.totalProventos
                                                        ) * 100
                                                    )
                                                )}
                                                <br />
                                                {moeda(
                                                    Math.round(
                                                        Number(
                                                            f.totalDescontos
                                                        ) * 100
                                                    )
                                                )}
                                                <br />
                                                {moeda(
                                                    Math.round(
                                                        Number(f.totalLiquido) *
                                                            100
                                                    )
                                                )}
                                            </>
                                        ) : (
                                            "Consultar ocorrência / pendência"
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {o ? (
                                            <>
                                                <Link
                                                    component={RouterLink}
                                                    to={"/eventos/" + o.id}
                                                >
                                                    #{o.id}
                                                </Link>{" "}
                                                ·{" "}
                                                {resumo?.concluida
                                                    ? "Concluída no TCE-MS"
                                                    : resumo?.arquivada
                                                    ? "Arquivada"
                                                    : !resumo
                                                    ? "Atualize a preparação para conferir a situação"
                                                    : autorizadas.includes(o.id)
                                                    ? "Autorizada — acompanhar processamento"
                                                    : estado ===
                                                      "PROCESSADO_COM_SUCESSO"
                                                    ? resumo?.destino ===
                                                      "FORNECEDOR"
                                                        ? "Recebida pelo fornecedor — aceite do TCE-MS pendente"
                                                        : "Abertura aceita — conferir finalização"
                                                    : estado ===
                                                      "AGUARDANDO_APROVACAO"
                                                    ? "Pronta para autorização"
                                                    : estado}
                                            </>
                                        ) : (
                                            <>
                                                <Typography color="error">
                                                    Bloqueada
                                                </Typography>
                                                {(f.melhorias || []).map(
                                                    (m, i) => (
                                                        <Typography
                                                            key={i}
                                                            variant="caption"
                                                            display="block"
                                                        >
                                                            {m}
                                                        </Typography>
                                                    )
                                                )}
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {!ambientePermitido && (
                <Alert severity="warning">
                    A autorização de envio está indisponível em{" "}
                    {obterAmbienteAplicacao(ambiente).rotulo}. Esta versão
                    permite autorização somente em HOM/MOCK; a transmissão em
                    produção ainda não está habilitada.
                </Alert>
            )}
            {!perfilPermitido && (
                <Alert severity="warning">
                    A autorização exige autenticação e perfil Folha ou Master.
                </Alert>
            )}
            {!destinoDisponivel && (
                <Alert severity="warning">
                    Modo de envio: {modo.rotulo}. Consulte o status do sistema
                    antes de autorizar.
                </Alert>
            )}
            {!consultaDisponivel && (
                <Alert severity="warning">
                    Aguarde a consulta atual das ocorrências. Se a falha
                    persistir, atualize a preparação antes de selecionar.
                </Alert>
            )}
            <TextField
                multiline
                label="Justificativa da autorização do conjunto"
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
                inputProps={{maxLength: 500}}
                helperText="Entre 10 e 500 caracteres. Confira o ambiente e o modo de envio antes de autorizar."
            />
            <Button
                variant="contained"
                disabled={
                    !permiteAutorizar ||
                    !ids.length ||
                    !justificativaValida ||
                    autorizar.isLoading
                }
                onClick={enviar}
            >
                {permiteAutorizar
                    ? `Autorizar seleção em ${
                          obterAmbienteAplicacao(ambiente).codigo
                      } (${ids.length})`
                    : "Autorização indisponível"}
            </Button>
            {erro && <Alert severity="error">{erro}</Alert>}
            {!!autorizadas.length && (
                <Alert severity="success">
                    Seleção autorizada. Acompanhe a finalização de cada remessa
                    pelos links das ocorrências.
                </Alert>
            )}
        </Stack>
    );
}
